/**
 * ステップ進捗バー（プログレスバー形式）
 *
 * CreateUserWizardのStepProgressBarと同じデザイン
 * - 完了: ブランドグリーン (#2bdc98)
 * - 現在: ブランドグリーン (#2bdc98)
 * - 未完了: グレー (#c2c9cf)
 */
import I18n from "features/shared/i18n";
import { PlayerWizardStep, PlayerStepDefinition } from "../types";

// カラー定数（アプリ準拠）- 全部グリーン系
const COLORS = {
  completed: "#2bdc98", // ブランドグリーン
  completedDark: "#25c488", // グラデーション用
  current: "#2bdc98", // ブランドグリーン
  currentDark: "#25c488", // グラデーション用
  pending: "#c2c9cf", // グレー
  pendingLight: "#e0e4e7", // 薄いグレー
};

// サイズ定数
const BAR_WIDTH = 550;
const BAR_HEIGHT = 24;
const DOT_SIZE = 36;

interface PlayerStepProgressBarProps {
  steps: PlayerStepDefinition[];
  currentStep: PlayerWizardStep;
  onStepClick?: (step: PlayerWizardStep) => void;
}

// PROFILE_AI/PRESET_ICON/UPLOAD_ICONはICON_METHODステップとして表示
const getDisplayStep = (step: PlayerWizardStep): PlayerWizardStep => {
  if (step === PlayerWizardStep.PROFILE_AI) return PlayerWizardStep.ICON_METHOD;
  if (step === PlayerWizardStep.PRESET_ICON) return PlayerWizardStep.ICON_METHOD;
  if (step === PlayerWizardStep.UPLOAD_ICON) return PlayerWizardStep.ICON_METHOD;
  return step;
};

export const PlayerStepProgressBar = ({
  steps,
  currentStep,
  onStepClick,
}: PlayerStepProgressBarProps) => {
  const currentIndex = steps.findIndex((s) => s.key === getDisplayStep(currentStep));

  // セクション幅を計算
  const sectionWidth = BAR_WIDTH / steps.length;

  const handleStepClick = (index: number) => {
    // 完了済みステップのみクリック可能
    if (index < currentIndex && onStepClick) {
      onStepClick(steps[index].key);
    }
  };

  return (
    <div className="flex-col-el items-center w-full pb-6">
      {/* プログレスバー（浮いてる感じ） */}
      <div
        className="relative rounded-full"
        style={{
          width: BAR_WIDTH,
          height: BAR_HEIGHT,
          backgroundColor: COLORS.pendingLight,
          border: "3px solid white",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1)",
        }}
      >
        {/* 完了セクション（立体グラデーション） */}
        {currentIndex > 0 && (
          <div
            className="absolute left-0 top-0 h-full"
            style={{
              width: sectionWidth * currentIndex,
              background: "linear-gradient(180deg, #3ee8a8 0%, #2bdc98 40%, #25c488 100%)",
              borderRadius: "9999px 0 0 9999px",
            }}
          />
        )}
        {/* 現在セクション - 進捗アニメーション（左から右） */}
        <div
          className="absolute top-0 h-full"
          style={{
            left: sectionWidth * currentIndex,
            width: sectionWidth,
            borderRadius:
              currentIndex === 0
                ? "9999px 0 0 9999px"
                : currentIndex === steps.length - 1
                  ? "0 9999px 9999px 0"
                  : "0",
            overflow: "hidden",
          }}
        >
          <div
            className="h-full step-bar-progress"
            style={{
              background: "linear-gradient(180deg, #3ee8a8 0%, #2bdc98 40%, #25c488 100%)",
              clipPath: "polygon(0 0, 100% 0, calc(100% - 6px) 100%, 0 100%)",
            }}
          />
        </div>
        {/* セクション間の区切り線（斜め） */}
        {steps.slice(1).map((_, index) => (
          <div
            key={index}
            className="absolute top-0 h-full"
            style={{
              left: sectionWidth * (index + 1) - 1.5,
              width: 3,
              backgroundColor: "white",
              transform: "skewX(-15deg)",
            }}
          />
        ))}
      </div>

      {/* ステップマーカーとラベル */}
      <div className="flex-row-el justify-between mt-3" style={{ width: BAR_WIDTH + 40 }}>
        {steps.map((step, index) => {
          const isCompleted = index < currentIndex;
          const isCurrent = index === currentIndex;

          return (
            <div
              key={step.key}
              className={`flex-col-el items-center ${isCompleted ? "cursor-pointer" : ""}`}
              style={{ width: sectionWidth + 40 / steps.length }}
              onClick={() => handleStepClick(index)}
            >
              {/* ドットマーカー */}
              <div
                className={`relative flex-col-el flex-center rounded-full ${isCompleted ? "step-completed" : ""} ${isCurrent ? "step-pulse" : ""}`}
                style={{
                  width: DOT_SIZE,
                  height: DOT_SIZE,
                  backgroundColor: isCompleted || isCurrent ? COLORS.current : "transparent",
                  border: !isCompleted && !isCurrent ? `3px solid ${COLORS.pending}` : "none",
                }}
              >
                {/* パルスリング（現在ステップのみ） */}
                {isCurrent && <div className="step-current-ring" />}
                {isCompleted && (
                  <span
                    className="material-symbols-outlined text-[22px]"
                    style={{ color: "#FFFFFF" }}
                  >
                    check
                  </span>
                )}
                {isCurrent && (
                  <span className="text-[20px]" style={{ color: "#FFFFFF" }}>
                    {index + 1}
                  </span>
                )}
                {!isCompleted && !isCurrent && (
                  <span className="text-[20px]" style={{ color: COLORS.pending }}>
                    {index + 1}
                  </span>
                )}
              </div>

              {/* ラベル */}
              <p
                className="text-body text-[28px] whitespace-nowrap mt-3"
                style={{
                  color: isCompleted || isCurrent ? COLORS.current : COLORS.pending,
                }}
              >
                {I18n.t(step.labelKey)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
